import { Flex, Form, notification } from "antd";
import { type FC, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { postNetworkCreate } from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";

import BaseButton from "@/components/BaseButton";
import { BaseModal } from "@/components/BaseModal";
import { RHFTextField } from "@/components/RHF/RHFTextField";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch } from "@/hooks/redux";
import { setMarketCreatePageModalSelectionPayload } from "@/store/slices/ui";
import { NewNetworkFormValues } from "@/types/form";

interface AddNetworkModalProps {
    onClose: () => void;
}

const emptyForm: NewNetworkFormValues = {
    network: "",
};

export const AddNetworkModal: FC<AddNetworkModalProps> = ({ onClose }) => {
    const dispatch = useAppDispatch();

    const addLegalEntityForm = useForm<NewNetworkFormValues>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: emptyForm,
    });

    const {
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = addLegalEntityForm;

    const onSubmit = useCallback(
        async ({ network }: NewNetworkFormValues) => {
            try {
                const { result: newNetwork } = await postNetworkCreate({
                    name: network,
                });

                dispatch(
                    setMarketCreatePageModalSelectionPayload({
                        fieldName: "network",
                        option: {
                            label: newNetwork.name,
                            value: newNetwork.id,
                        },
                        value: newNetwork.id,
                    }),
                );
            } catch (error) {
                console.error(error);

                notification.error({ message: "Не вдалося створити мережу" });
            }

            reset(emptyForm);

            onClose();
        },
        [dispatch, onClose, reset],
    );

    return (
        <BaseModal
            open
            onClose={() => !isSubmitting && onClose()}
            maskClosable={false}
            title={"Додати мережу"}
            actions={null}
        >
            <PrimaryText color="textDisabled">
                Для додавання мережі, заповніть форму
            </PrimaryText>

            <FormProvider {...addLegalEntityForm}>
                <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
                    <RHFTextField
                        name={"network"}
                        label={"Назва мережі"}
                        rules={{
                            required: validationMessage.required,
                        }}
                        disabled={isSubmitting}
                        style={{ marginBottom: 16 }}
                        size="large"
                    />

                    <Flex vertical gap={8}>
                        <BaseButton
                            size="large"
                            type="default"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Скасувати
                        </BaseButton>

                        <BaseButton
                            size="large"
                            type="primary"
                            htmlType="submit"
                            loading={isSubmitting}
                        >
                            Додати мережу
                        </BaseButton>
                    </Flex>
                </Form>
            </FormProvider>
        </BaseModal>
    );
};
